import React, { useMemo } from 'react';
import { Card, Tooltip } from 'antd';
import { FileSearchOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { addFavorite, removeFavorite } from '../../../store/tz/reducer';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '../../../theme/ThemeProvider';
import { getScoreCardWrapperStyle, getScoreCardInnerStyle } from '../../../theme/scoreBorder';

const { Meta } = Card;

const parseMktu = (data) => {
  const raw = data?.['Nice Cl.'] || data?.['MARKGR/CURRENT/BASICGS@NICEVER'] || '';
  const list = raw ? Array.from(new Set(String(raw).match(/\d+/g))) : null;
  if (!list || !list.length) return '—';
  return list.length > 3 ? `${list.slice(0, 3).join(', ')}…` : list.join(', ');
};

const WipoTrademarkCard = ({ data = {}, base64Image, score, requestType, searchQuery, onClick }) => {
  const { darkMode } = useTheme();
  const dispatch = useDispatch();
  const favorites = useSelector((s) => s.tz.favorites);

  const cardId = useMemo(() => {
    const byReg = data?.['Reg. No'] || data?.['@INTREGN'];
    if (byReg) return String(byReg);
    return uuidv4();
  }, [data]);

  const isFavorite = favorites.some((f) => f.id === cardId);

  const toggleFavorite = (e) => {
    e.stopPropagation();
    if (isFavorite) dispatch(removeFavorite(cardId));
    else dispatch(addFavorite({ id: cardId, searchQuery, document: data, imgSrc: base64Image ? `data:image/jpg;base64,${base64Image}` : null, score, request_type: 'wipo' }));
  };

  const imgSrc = base64Image ? `data:image/jpg;base64,${base64Image}` : null;
  const statusRaw = String(data?.Status || data?.['MARKGR@STATUS'] || '').toLowerCase();
  
  // Логика определения статуса как в DocumentCard
  const isActive = statusRaw && !statusRaw.match(/(отказ|прекрат|отозван|отклонен|завершен|expired|cancelled|abandoned)/i);
  
  // Проверка на истекший срок действия
  const isExpired = useMemo(() => {
    const expirationDate = data?.['Exp. Date'] || data?.['MARKGR/CURRENT/EXPIRATION_DATE'];
    if (!expirationDate) return false;
    
    let dateToParse = expirationDate;
    if (Array.isArray(expirationDate)) {
      dateToParse = expirationDate[0];
    }
    
    const expiration = new Date(dateToParse);
    const now = new Date();
    if (isNaN(expiration.getTime())) {
      return false;
    }
    return expiration < now;
  }, [data]);
  
  // Определение текста и стиля статуса
  let statusText = '';
  let statusStyle = {
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: '',
    color: '#fff',
    fontSize: 14,
    borderRadius: 4,
    padding: '2px 8px',
    marginBottom: 4,
  };
  
  if (isExpired) {
    statusText = 'ИСТЕК/ИСТЕКАЕТ СРОК!';
    statusStyle.backgroundColor = 'rgba(158, 152, 102, 0.8)';
  } else if (isActive) {
    statusText = 'Действует';
    statusStyle.backgroundColor = 'rgba(40,167,69,0.6)';
    delete statusStyle.animation;
  } else {
    statusText = 'Не действует';
    statusStyle.backgroundColor = 'rgba(108,117,125,0.6)';
    delete statusStyle.animation;
  }
  
  const country =
    data?.Origin
    || data?.['Holder country']
    || data?.['MARKGR/CURRENT/HOLGR/ADDRESS/COUNTRY']
    || data?.['@OOCD']
    || data?.['MARKGR@OOCD']
    || '';
  const holder =
    data?.Holder
    || data?.['MARKGR/CURRENT/HOLGR/NAME']
    || data?.['MARKGR/CURRENT/HOLGR']
    || '';
  const title =
    data?.Trademark
    || data?.['MARKGR/CURRENT/IMAGE@TEXT']
    || data?.['MARKGR/CURRENT/REPGR/NAME']
    || data?.['@INTREGN']
    || 'Без названия';
  const mktuShort = parseMktu(data);

  const containerStyle =
    typeof score === 'number'
      ? getScoreCardWrapperStyle(score, darkMode)
      : {
          position: 'relative',
          borderRadius: 8,
          border: darkMode ? '1px solid var(--theme-border)' : undefined,
          background: 'var(--theme-surface)',
          boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.25)' : '0 2px 8px rgba(0,0,0,0.1)',
        };

  const cardStyle = getScoreCardInnerStyle(darkMode, {
    borderRadius: 6,
    width: '100%',
    height: 'auto',
    overflow: 'visible',
  });

  return (
    <div style={containerStyle}>
      <Card
        hoverable
        bordered={false}
        style={cardStyle}
        cover={
          <div style={{ cursor: 'pointer' }} onClick={onClick}>
            {imgSrc ? (
              <img src={imgSrc} alt={data?.Trademark || 'Без названия'} style={{ width: '100%', height: 150, objectFit: 'contain', borderTopLeftRadius: 6, borderTopRightRadius: 6 }} />
            ) : (
              <div style={{ width: '100%', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileSearchOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </div>
            )}
            {/* цветовая плашка статуса */}
            <div style={statusStyle}>{statusText}</div>
          </div>
        }
      >
        <Meta
          title={title}
          description={
            <>
              <div style={{ marginBottom: 2 }}>
                <b>Страна:</b> {country || '—'}
              </div>
              <div style={{ marginBottom: 2 }}>
                <b>Правообладатель:</b> {holder || '—'}
              </div>
              <div style={{ marginBottom: 2 }}>
                <b>Классы МКТУ:</b> <Tooltip title={mktuShort}>{mktuShort}</Tooltip>
              </div>
            </>
          }
        />
      </Card>
      <div onClick={toggleFavorite} style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', zIndex: 1 }}>
        {isFavorite ? (
          <StarFilled style={{ color: '#fadb14', fontSize: 24 }} />
        ) : (
          <StarOutlined style={{ color: '#fadb14', fontSize: 24 }} />
        )}
      </div>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

export default React.memo(WipoTrademarkCard);


